import template from 'url-template';

import { ConcurrencyManager } from 'axios-concurrency';
import { get } from 'lodash';
import axios from 'axios';

import {
    SET_AM_ENTITIES,
    SET_AM_SELECTED_ATTRIBUTE_VALUE,
    SET_AM_SELECTED_STATE,
    SET_CUSTOM_ATTRIBUTES,
    FETCH_ATTRIBUTES,
    FETCH_ATTRIBUTE_VALUES,
    FETCH_FACETS,
    FETCH_SOURCES
} from './actionTypes';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';

import { enqueueSnackbar, handleNetworkError } from './applicationMessageActions';

const AttributeUrl = template.parse('/attributes');
const AttributeValueUrl = template.parse('/companies/{companyId}/attributes/{attributeId}/lookups{?includeSyndicated}');
const EntitiesUrl = template.parse('/entities');
const FacetsUrl = template.parse('/facets');
const ClientsUrl = template.parse('/companies/{companyId}/clients{?limit}');
const UpdateClientUrl = template.parse('/clients/{clientId}');
const CustomAttributeUrl = template.parse('/attributes/custom-attributes{?dimensionId}');

const MAX_CONCURRENCY_REQUESTS = 10;

export const fetchAttributes = () => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            AttributeUrl.expand({}),
            header
        );

        let payload = get(response, 'data', []);

        dispatch({
            type: FETCH_ATTRIBUTES,
            payload
        });

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCustomAttributes = () => async (dispatch, getState) => {
    const header = await awsHeader();

    const dimensions = getState().attributeManagement.entities;

    try {
        let promises = [];
        let payload = {};

        const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
        dimensions.forEach(item => {
            let options = { dimensionId: item.id };
            promises.push(k2aBaseApi.get(CustomAttributeUrl.expand({ ...options }), header));
        });

        await Promise.all(promises.map(p => p.catch(err => err)))
            .then(axios.spread((...result) => {
                result.forEach((r, i) => {
                    const dimension = dimensions[i];
                    payload[dimension.name] = r.data;
                });
            }));

        manager.detach();

        dispatch({
            type: SET_CUSTOM_ATTRIBUTES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchAttributeValues = (companyId, attributeId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.includeSyndicated = true;

    try {
        const response = await k2aBaseApi.get(
            AttributeValueUrl.expand({
                companyId,
                attributeId,
                ...options
            }),
            header
        );

        const payload = get(response, 'data', []);
        payload.forEach(item => item.name = item.value);

        dispatch({
            type: FETCH_ATTRIBUTE_VALUES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSources = (companyId, attributeId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.includeSyndicated = true;

    try {
        const response = await k2aBaseApi.get(
            AttributeValueUrl.expand({
                companyId,
                attributeId,
                ...options
            }),
            header
        );

        const payload = get(response, 'data', []);
        payload.forEach(item => item.name = item.value);

        dispatch({
            type: FETCH_SOURCES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEntities = () => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            EntitiesUrl.expand({}),
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: SET_AM_ENTITIES,
            payload
        });

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchFacets = () => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            FacetsUrl.expand({}),
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: FETCH_FACETS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const createAttributeValue = (companyId, attributeId, body) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            AttributeValueUrl.expand({
                companyId,
                attributeId
            }),
            body,
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateAttributeValues = (companyId, items, entityId, attributeId) => async (dispatch, getState) => {
    const header = await awsHeader();
    let body = { companyAVPs: []};

    try {
        const facets = getState().attributeManagement.facets;
        const order = facets.find(facet => facet.value === 'order');
        const selectedFacet = facets.find(facet => facet.value === 'selected');

        const selected = items.filter(item => item.available === false && item.id !== 'select');
        const unselected = items.filter(item => item.available === true && item.id !== 'select');

        selected.forEach((item, index) => {
            if (item.id === 'select' || (item.companyId === '00000000-0000-0000-0000-000000000000' && item.available)) return;
            body.companyAVPs.push({
                id:  item.companyId === '00000000-0000-0000-0000-000000000000' ? null : item.companyAVPId || null, //Nullable
                avp: {
                    id:    item.id,
                    value: item.name
                },
                entityId: entityId, //MOSTLY GLOBAL
                facets:   [
                    {
                        id:    selectedFacet.id,
                        value: item.available ? '0' : '1'
                    },
                    {
                        id:    order.id, //ID FOR ORDER,
                        value: index
                    }
                ]
            });
        });

        unselected.forEach(item => {
            if (item.id === 'select' || (item.companyId === '00000000-0000-0000-0000-000000000000' && item.available)) return;
            body.companyAVPs.push({
                id:  item.companyAVPId || null, //Nullable
                avp: {
                    id:    item.id,
                    value: item.name
                },
                entityId: entityId, //MOSTLY GLOBAL
                facets:   [
                    {
                        id:    selectedFacet.id,
                        value: item.available ? '0' : '1'
                    },
                ]
            });
        });

        await k2aBaseApi.post(
            AttributeValueUrl.expand({
                companyId,
                attributeId
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Attribute values have been updated successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchAMClients = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit = 1000;

    try {
        const payload = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const clients = get(payload, 'data.data', []);

        dispatch({
            type:    SET_AM_ENTITIES,
            payload: clients
        });

        return clients;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const updateAMClient = client => async (dispatch, getState) => {
    const header = await awsHeader();

    const clientId = client.id;

    try {
        await k2aBaseApi.put(
            UpdateClientUrl.expand({
                clientId
            }),
            client,
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchAMSites = companyId => async (dispatch, getState) => {

};

export const setAMSelectedState = state => async (dispatch, getState) => {
    dispatch({
        type:    SET_AM_SELECTED_STATE,
        payload: state
    });
};

export const setAMSelectedAttributeValue = value => async (dispatch, getState) => {
    dispatch({
        type:    SET_AM_SELECTED_ATTRIBUTE_VALUE,
        payload: value
    });
};
